import { useCallback, useEffect, useState } from 'react'
import { logError } from '@/utils/errorUtils'

export type RecentPatient = {
  id: string
  name: string
}

const STORAGE_KEY = 'behuman_recent_patients'
const MAX_RECENT = 5
const STORAGE_EVENT = 'behuman_recent_patients_changed'

function readStorage(): RecentPatient[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (p): p is RecentPatient =>
        typeof p === 'object' &&
        p !== null &&
        typeof p.id === 'string' &&
        typeof p.name === 'string',
    )
  } catch (err) {
    logError('shell', 'Failed to parse recent patients from localStorage', err)
    return []
  }
}

function writeStorage(list: RecentPatient[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
    window.dispatchEvent(new Event(STORAGE_EVENT))
  } catch (err) {
    logError('shell', 'Failed to persist recent patients to localStorage', err)
  }
}

/**
 * Clears the persisted recent-patients list. Called on logout so patient names
 * (PHI) don't outlive the session on a shared workstation.
 */
export function clearRecentPatients(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
    window.dispatchEvent(new Event(STORAGE_EVENT))
  } catch (err) {
    logError('shell', 'Failed to clear recent patients from localStorage', err)
  }
}

/**
 * Tracks the last few patients the current user has viewed.
 *
 * Persisted in localStorage (per-browser, per-user-device — not synced with
 * the backend). Capped at 5 entries; most-recent first. Components listening
 * to the same hook stay in sync via a custom `behuman_recent_patients_changed`
 * window event.
 */
export function useRecentPatients() {
  const [recent, setRecent] = useState<RecentPatient[]>(() => readStorage())

  useEffect(() => {
    const refresh = () => setRecent(readStorage())
    window.addEventListener(STORAGE_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(STORAGE_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  const recordPatient = useCallback((patient: RecentPatient) => {
    if (!patient.id || !patient.name) return
    const next = [patient, ...readStorage().filter((p) => p.id !== patient.id)].slice(0, MAX_RECENT)
    writeStorage(next)
    setRecent(next)
  }, [])

  return { recent, recordPatient }
}
